import { zodResolver } from "@hookform/resolvers/zod";
import { useVehicles } from "api/vehicle.api";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { TransportSedel } from "types/transportSedel.type";
import { vehicleTypeToName } from "types/vehicle.type";
import { z } from "zod";
import { usePatchTransportSedelSheet } from "./usePatchTransportSedelSheet";

const vehicleSchema = z.object({
  vehicleUuid: z
    .string({
      required_error: "Obligatoriskt fält",
      message: "Obligatoriskt fält",
    })
    .uuid(),
});
type TVehicleSchema = z.infer<typeof vehicleSchema>;

export const useUpdateVehicleLogic = (transportSedel: TransportSedel) => {
  const sheet = usePatchTransportSedelSheet();

  const form = useForm<TVehicleSchema>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      vehicleUuid: transportSedel.vehicle?.uuid,
    },
  });

  useEffect(() => {
    if (sheet.isModalOpen) {
      form.reset({ vehicleUuid: transportSedel.vehicle?.uuid });
    }
  }, [form, transportSedel, sheet.isModalOpen]);

  const onSubmit = form.handleSubmit((data: TVehicleSchema) => {
    sheet.mutation.mutate({
      id: transportSedel.id,
      payload: { vehicleUuid: data.vehicleUuid },
    });
  });

  const { data: vehicles } = useVehicles();
  const vehicleDropdownItems = useMemo(
    () =>
      (vehicles ?? [])
        .map(({ uuid, registrationNumber, type }) => ({
          label: `${registrationNumber} - ${vehicleTypeToName(type)}`,
          value: uuid,
        }))
        .sort((a, b) => (a.label < b.label ? -1 : 1)),
    [vehicles],
  );

  return {
    isModalOpen: sheet.isModalOpen,
    openModal: sheet.openModal,
    onModalClose: sheet.onModalClose,
    form,
    onSubmit,
    isPending: sheet.mutation.isPending,
    vehicleDropdownItems,
  };
};

export type UpdateVehicleLogicRetVal = ReturnType<typeof useUpdateVehicleLogic>;
